import { useState, type FunctionComponent } from "react";
import { Button, Input, Select, Space, Typography, message } from "antd";
import { DeleteOutlined, SaveOutlined } from "@ant-design/icons";

import { useTraineeSegments } from "./useTraineeSegments";
import { isFilterActive, type TraineeTargetSpec } from "./types";

type Props = {
  currentSpec: TraineeTargetSpec;
  onLoad: (spec: TraineeTargetSpec) => void;
};

export const TraineeSegmentsBar: FunctionComponent<Props> = ({ currentSpec, onLoad }) => {
  const { segments, saveSegment, deleteSegment, isSaving } = useTraineeSegments();
  const [name, setName] = useState("");

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      void message.warning("Podaj nazwę segmentu");
      return;
    }
    if (!isFilterActive(currentSpec)) {
      void message.warning("Najpierw ustaw filtr (produkt / tag / status), aby zapisać segment");
      return;
    }
    try {
      await saveSegment({ name: trimmed, spec: currentSpec });
      setName("");
      void message.success("Segment zapisany");
    } catch (error) {
      void message.error(error instanceof Error ? error.message : "Nie udało się zapisać segmentu");
    }
  };

  return (
    <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8, padding: 12 }}>
      <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
        Segmenty (zapisane filtry)
      </Typography.Text>
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Select
          allowClear
          showSearch
          placeholder="Wczytaj zapisany segment..."
          style={{ width: "100%" }}
          value={null}
          options={segments.map((segment) => ({ value: segment.id, label: segment.name }))}
          filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
          onChange={(id: string | null) => {
            const segment = segments.find((item) => item.id === id);
            if (segment) {
              onLoad(segment.spec);
            }
          }}
          optionRender={(option) => (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{option.label}</span>
              <DeleteOutlined
                style={{ color: "#cf1322" }}
                onClick={(event) => {
                  event.stopPropagation();
                  deleteSegment(String(option.value)).catch((error: unknown) => {
                    void message.error(
                      error instanceof Error ? error.message : "Nie udało się usunąć segmentu",
                    );
                  });
                }}
              />
            </div>
          )}
        />
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="Nazwa nowego segmentu"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onPressEnter={handleSave}
          />
          <Button icon={<SaveOutlined />} loading={isSaving} onClick={handleSave}>
            Zapisz
          </Button>
        </Space.Compact>
      </Space>
    </div>
  );
};

export default TraineeSegmentsBar;
